import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import PriorInteractions from "@/components/PriorInteractions";
import SpinnerButton from "@/components/SpinnerButton";
import TeamLayout from "@/components/TeamLayout";
import WonDealFields from "@/components/WonDealFields";
import { teamApi, apiBase } from "@/lib/api";
import { waLink } from "@/lib/phone";

// Team-side mirror of AdminLeads. Identical filter shape and pagination
// contract — the ONLY differences are:
//   1. No Duty-Person filter (server hard-scopes to me; admin can pick
//      across all members, team cannot).
//   2. No "+ Add lead" button (manual entry lives on /team/log).
//   3. Drawer goes read-only for Closed Won / Closed Lost (matches the
//      TeamPanel drawer rules — closed-deal economics already fired
//      to Meta CAPI + Google Ads, reopening must stay admin-only).
const TYPES = ["All", "Email", "WhatsApp", "Appointment", "Walk-in"];
const STATUSES = ["All", "New", "Contacted", "In Progress", "Closed Won", "Closed Lost"];
const ALLOWED_STATUSES = ["New", "Contacted", "In Progress", "Closed Won", "Closed Lost"];
const CLOSED_STATUSES = ["Closed Won", "Closed Lost"];
const PAGE_SIZE = 50;

// Mirrors LOST_REASONS in backend/server.py, which validates the value
// on save. Hardcoded here the same way STATUSES already is — keep the
// two lists in step.
const LOST_REASONS = ["Price", "Bought elsewhere", "Stock unavailable", "No response from customer", "Not a serious buyer", "Other"];

function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function quickRange(kind) {
  const now = new Date();
  if (kind === "today") return [ymd(now), ymd(now)];
  if (kind === "week") {
    const dow = (now.getDay() + 6) % 7;
    const start = new Date(now); start.setDate(now.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return [ymd(start), ymd(end)];
  }
  if (kind === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [ymd(start), ymd(end)];
  }
  if (kind === "30d") {
    const start = new Date(now); start.setDate(now.getDate() - 29);
    return [ymd(start), ymd(now)];
  }
  if (kind === "90d") {
    const start = new Date(now); start.setDate(now.getDate() - 89);
    return [ymd(start), ymd(now)];
  }
  return ["", ""];
}

function Drawer({ lead, adminEmail, onClose, onUpdate }) {
  const readOnly = CLOSED_STATUSES.includes(lead.Status);
  const [status, setStatus] = useState(lead.Status);
  const [note, setNote] = useState("");
  const [apptDate, setApptDate] = useState(lead.Appointment_Date || "");
  const [apptTime, setApptTime] = useState(lead.Appointment_Time || "");
  const [wonValue, setWonValue] = useState(lead.Won_Value || "");
  const [wonSku, setWonSku] = useState(lead.Won_SKU || "");
  const [lostReason, setLostReason] = useState(lead.Lost_Reason || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr(""); setLoading(true);
    try {
      const body = { status, note };
      if (lead.Type === "Appointment") { body.appointment_date = apptDate; body.appointment_time = apptTime; }
      if (status === "Closed Won") { body.won_value = wonValue; body.won_sku = wonSku; }
      if (status === "Closed Lost") { body.lost_reason = lostReason; }
      const res = await teamApi.leadUpdate(lead.ID, body);
      onUpdate(res.data.lead);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", justifyContent: "flex-end", zIndex: 50 }} onClick={onClose} data-testid="team-leads-drawer">
      <div style={{ width: "min(560px, 100%)", background: "#FFFFFF", height: "100%", overflowY: "auto", padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <p className="scw-eyebrow" data-testid="team-leads-drawer-mode">
            Lead detail{readOnly ? " \u00b7 read-only" : ""}
          </p>
          <button onClick={onClose} className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} data-testid="team-leads-drawer-close">Close</button>
        </div>
        <h2 style={{ fontSize: 24, margin: "0 0 18px 0" }}>{lead.Customer_Name}</h2>
        <PriorInteractions leadId={lead.ID} apiClient={teamApi} testIdPrefix="team" />
        {readOnly && (
          <div
            style={{ background: "#fff7e6", border: "1px solid #f0d9a1", padding: "10px 14px", fontSize: 12, color: "#7a5a13", marginBottom: 18 }}
            data-testid="team-leads-drawer-readonly-banner"
          >
            This lead is closed. Review-only view for team {"\u2014"} reopening or
            editing a closed deal is admin-only (the conversion value has
            already been sent to Meta &amp; Google).
            {adminEmail && (
              <>
                {" "}Need to amend this deal?{" "}
                <a
                  href={`mailto:${adminEmail}?subject=${encodeURIComponent(
                    `Amend closed lead — ${lead.Customer_Name} (${lead.ID})`
                  )}`}
                  style={{ color: "#7a5a13", textDecoration: "underline" }}
                  data-testid="team-leads-drawer-amend-mailto"
                >Message {adminEmail}</a>.
              </>
            )}
          </div>
        )}
        {/* Pass 55 — the review request no longer lives in this drawer.
            Reviews have their own admin tab, and a lead only becomes
            review-eligible 3 days after the win, so the drawer was
            always either too early or stale. */}
        <div style={{ borderTop: "1px solid var(--scw-line)", marginBottom: 24 }}>
          {[
            ["Type", lead.Type],
            ["Phone", lead.Customer_Phone
              ? (waLink(lead.Customer_Phone)
                ? <a href={waLink(lead.Customer_Phone)} target="_blank" rel="noreferrer" data-testid="team-leads-drawer-wa-link">{lead.Customer_Phone}</a>
                : lead.Customer_Phone)
              : "\u2014"],
            ["Email", lead.Customer_Email || "\u2014"],
            ["Product", lead.Product_Watch || "\u2014"], ["Message", lead.Message || "\u2014"],
            ["Appointment", lead.Appointment_Date ? `${lead.Appointment_Date} ${lead.Appointment_Time}` : "\u2014"],
            ["Received", lead.Timestamp + " MYT"], ["Last update", lead.Last_Updated + " MYT"],
            ["Source", lead.Source || "\u2014"],
            // marketing_source is server-resolved (Pass 53): raw UTM_Source
            // with a gclid/fbclid-based override on blank/"shopify" values,
            // same logic backing the Analytics "by marketing source" chart \u2014
            // see _resolve_marketing_source() in backend/server.py.
            ["Marketing source", lead.marketing_source || "\u2014"],
            ["Won value (MYR)", lead.Won_Value || "\u2014"],
            ["Won SKU", lead.Won_SKU || "\u2014"],
            ["Won date", lead.Won_Date ? `${lead.Won_Date} MYT` : "\u2014"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", padding: "10px 0", borderBottom: "1px solid var(--scw-line)" }}>
              <div style={{ width: 130, color: "var(--scw-muted)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>{k}</div>
              <div style={{ flex: 1, whiteSpace: "pre-wrap" }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 18 }}>
          <label className="scw-label">Status</label>
          <select
            className="scw-select"
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              if (next === "Closed Won" && !wonSku && lead.Product_Watch) {
                setWonSku(lead.Product_Watch);
              }
            }}
            disabled={readOnly}
            data-testid="team-leads-drawer-status"
          >
            {ALLOWED_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <WonDealFields
          visible={status === "Closed Won"}
          wonValue={wonValue}
          wonSku={wonSku}
          onChangeValue={setWonValue}
          onChangeSku={setWonSku}
          readOnly={readOnly}
        />
        {/* Pass 56 — required on the flip to Closed Lost. The server
            validates it too; this is the affordance, not the gate. */}
        {status === "Closed Lost" && (
          <div style={{ marginBottom: 18 }}>
            <label className="scw-label">Reason lost</label>
            <select
              className="scw-select"
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              disabled={readOnly}
              data-testid="team-leads-drawer-lost-reason"
            >
              <option value="">Select a reason…</option>
              {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
        {!readOnly && (
          <div style={{ marginBottom: 18 }}>
            <label className="scw-label">Append note</label>
            <textarea className="scw-textarea" value={note} onChange={(e) => setNote(e.target.value)} data-testid="team-leads-drawer-note" />
          </div>
        )}
        {lead.Type === "Appointment" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
            <div>
              <label className="scw-label">Appointment date</label>
              <input className="scw-input" type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} disabled={readOnly} data-testid="team-leads-drawer-apptdate" />
            </div>
            <div>
              <label className="scw-label">Appointment time</label>
              <input className="scw-input" type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)} disabled={readOnly} data-testid="team-leads-drawer-appttime" />
            </div>
          </div>
        )}
        {err && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }}>{err}</p>}
        {!readOnly && (
          <SpinnerButton loading={loading} type="button" onClick={save} dataTestId="team-leads-drawer-save">Save</SpinnerButton>
        )}

        <div style={{ marginTop: 32 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Notes</p>
          <pre style={{ background: "#fafafa", padding: 14, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }}>{lead.Notes || "\u2014"}</pre>
        </div>
        <div style={{ marginTop: 16 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Status history</p>
          <pre style={{ background: "#fafafa", padding: 14, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }}>{lead.Status_History || "\u2014"}</pre>
        </div>
      </div>
    </div>
  );
}

export default function TeamLeads() {
  const [auth, setAuth] = useState({ loading: true, ok: false });
  const [adminEmail, setAdminEmail] = useState("");
  const [filters, setFilters] = useState({ type: "All", status: "All", date_from: "", date_to: "", q: "" });
  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  // Pass 46 — `?open=<lead_id>` route param from the dashboard
  // pending list (or any deep link) auto-opens that lead's drawer
  // once the leads payload loads. We consume the param exactly once
  // per mount to avoid re-opening if the user closes the drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const openLeadId = searchParams.get("open") || "";

  useEffect(() => {
    teamApi.me()
      .then(() => setAuth({ loading: false, ok: true }))
      .catch(() => setAuth({ loading: false, ok: false }));
  }, []);

  // Pull admin_email from the dashboard payload so the read-only banner
  // mailto link works here too. Single shared fetch — the dashboard
  // payload is already cached server-side via the All_Leads single-flight.
  useEffect(() => {
    if (!auth.ok) return;
    teamApi.dashboard()
      .then((r) => setAdminEmail(r.data?.admin_email || ""))
      .catch(() => setAdminEmail(""));
  }, [auth.ok]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (filters.type !== "All") p.type = filters.type;
    if (filters.status !== "All") p.status = filters.status;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.q) p.q = filters.q;
    return p;
  }, [filters, page]);

  useEffect(() => { setPage(1); }, [filters]);

  const load = useCallback(async () => {
    if (!auth.ok) return;
    setLoading(true);
    try {
      const res = await teamApi.leads(params);
      setLeads(res.data.leads);
      setCount(res.data.count);
      setTotalPages(res.data.total_pages || 1);
    } finally { setLoading(false); }
  }, [auth.ok, params]);

  useEffect(() => { load(); }, [load]);

  // Auto-open the requested lead's drawer when the page mounts via
  // `/team/leads?open=<id>`. We fetch the row directly (the leads
  // payload may not contain it if it's outside the current filter /
  // page), then clear the search param so the user can close the
  // drawer without it reopening.
  useEffect(() => {
    if (!auth.ok || !openLeadId) return;
    let cancelled = false;
    teamApi.leadDetail(openLeadId)
      .then((r) => { if (!cancelled && r.data?.lead) setActive(r.data.lead); })
      .catch(() => { /* silently ignore — most likely a 404 from cross-member or stale link */ })
      .finally(() => { if (!cancelled) setSearchParams({}, { replace: true }); });
    return () => { cancelled = true; };
  }, [auth.ok, openLeadId, setSearchParams]);

  const exportCsv = () => {
    // CSV export uses the full SCOPED filtered set. Server-side hard
    // filter on Duty_Person == me ensures the file can't contain
    // another member's lead.
    const csvParams = { ...params };
    delete csvParams.page; delete csvParams.page_size;
    csvParams.format = "csv";
    const qs = new URLSearchParams(csvParams).toString();
    const url = `${apiBase}/team/leads?${qs}`;
    window.open(url, "_blank");
  };

  const applyQuickRange = (kind) => {
    const [from, to] = quickRange(kind);
    setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  };

  if (auth.loading) return <div style={{ padding: 40 }}>{"Loading\u2026"}</div>;
  if (!auth.ok) return <Navigate to="/team/login" replace />;

  return (
    <TeamLayout pageTitle="My Leads — SCW Contact Hub">
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Your enquiries</p>
      <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>My Leads</h1>

      <div className="scw-chip-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <p className="scw-eyebrow" style={{ margin: "auto 8px auto 0" }}>Quick range:</p>
        {[
          ["today", "Today"],
          ["week", "This Week"],
          ["month", "This Month"],
          ["30d", "Last 30 Days"],
          ["90d", "Last 90 Days"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "6px 14px", fontSize: 12 }}
            onClick={() => applyQuickRange(k)}
            data-testid={`team-leads-range-${k}`}
          >
            {label}
          </button>
        ))}
        {(filters.date_from || filters.date_to) && (
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "6px 14px", fontSize: 12, color: "var(--scw-muted)" }}
            onClick={() => setFilters((f) => ({ ...f, date_from: "", date_to: "" }))}
            data-testid="team-leads-range-clear"
          >Clear dates</button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div>
          <label className="scw-label">Type</label>
          <select className="scw-select" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="team-leads-filter-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">Status</label>
          <select className="scw-select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="team-leads-filter-status">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">From</label>
          <input type="date" className="scw-input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="team-leads-filter-from" />
        </div>
        <div>
          <label className="scw-label">To</label>
          <input type="date" className="scw-input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="team-leads-filter-to" />
        </div>
        <div>
          <label className="scw-label">Search</label>
          <input className="scw-input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name, phone, or email" data-testid="team-leads-filter-q" />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <span className="scw-eyebrow" data-testid="team-leads-count">{count} result{count === 1 ? "" : "s"}</span>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={exportCsv} className="scw-btn scw-btn-ghost" style={{ padding: "10px 18px" }} data-testid="team-leads-export-btn">Export CSV</button>
        </div>
      </div>

      <div className="scw-card" style={{ padding: 0 }}>
        {loading ? (
          <p style={{ padding: 24 }}>{"Loading\u2026"}</p>
        ) : leads.length === 0 ? (
          <p style={{ padding: 24, color: "var(--scw-muted)" }} data-testid="team-leads-empty">No leads match the current filters.</p>
        ) : (
          <div className="scw-table-wrap">
            <table className="scw-table scw-leads-table">
              <thead><tr>
                <th>Timestamp</th><th>Type</th><th>Customer</th><th>Phone</th><th>Product</th><th>Status</th><th>Flags</th>
              </tr></thead>
              <tbody>
                {leads.map((r) => (
                  <tr key={r.ID} onClick={() => setActive(r)} style={{ cursor: "pointer" }} data-testid={`team-leads-row-${r.ID}`}>
                    <td data-label="When">{r.Timestamp}</td>
                    <td data-label="Type">{r.Type}</td>
                    <td data-label="Customer">{r.Customer_Name}</td>
                    <td data-label="Phone">
                      {waLink(r.Customer_Phone) ? (
                        <a
                          href={waLink(r.Customer_Phone)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`team-leads-row-wa-link-${r.ID}`}
                        >{r.Customer_Phone}</a>
                      ) : r.Customer_Phone}
                    </td>
                    <td data-label="Product">{r.Product_Watch || "\u2014"}</td>
                    <td data-label="Status"><span className="scw-pill">{r.Status}</span></td>
                    <td data-label="Flags">
                      {r.review_pending && (
                        <span
                          className="scw-pill"
                          data-testid={`team-leads-review-chip-${r.ID}`}
                          style={{ background: "#fff0d4", color: "#7a5a13", borderColor: "#f0d9a1" }}
                        >Review</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {count > 0 && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }} data-testid="team-leads-pagination">
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "8px 16px" }}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="team-leads-prev-btn"
          >{'\u2190'} Previous</button>
          <span className="scw-eyebrow" data-testid="team-leads-page-label">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "8px 16px" }}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="team-leads-next-btn"
          >Next {'\u2192'}</button>
        </div>
      )}

      {active && (
        <Drawer
          lead={active}
          adminEmail={adminEmail}
          onClose={() => setActive(null)}
          onUpdate={(updated) => {
            setLeads((cur) => cur.map((r) => r.ID === updated.ID ? updated : r));
            setActive(updated);
          }}
        />
      )}
    </TeamLayout>
  );
}
